import * as THREE from 'three';

/**
 * Geometry utility functions shared across different measurement types
 */

/**
 * Calculate the area of a polygon using the Shoelace formula (2D projection on XY plane)
 * This works for both planar and non-planar polygons by projecting to the XY plane
 *
 * @param points Array of 3D points defining the polygon
 * @returns Area of the polygon in square units
 */
export function calculatePolygonArea2D(points: THREE.Vector3[]): number {
  if (points.length < 3) return 0;

  let area = 0;
  let j = points.length - 1;

  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[j];
    area += (p2.x + p1.x) * (p1.y - p2.y);
    j = i;
  }

  return Math.abs(area / 2);
}

/**
 * Calculate the area of a planar polygon in 3D space using cross products
 * This gives the true 3D area regardless of polygon orientation
 *
 * @param points Array of 3D points defining the planar polygon
 * @returns Area of the polygon in square units
 */
export function calculatePolygonArea3D(points: THREE.Vector3[]): number {
  if (points.length < 3) return 0;

  const areaVector = new THREE.Vector3(0, 0, 0);

  for (let i = 0; i < points.length; i++) {
    const v1 = points[i];
    const v2 = points[(i + 1) % points.length];
    const cross = new THREE.Vector3().crossVectors(v1, v2);
    areaVector.add(cross);
  }

  return areaVector.length() / 2;
}

/**
 * Check if a set of points forms a planar polygon
 *
 * @param points Array of 3D points
 * @param tolerance Maximum deviation from plane (default: 1e-6)
 * @returns true if all points lie on the same plane
 */
export function isPolygonPlanar(points: THREE.Vector3[], tolerance = 1e-6): boolean {
  if (points.length < 4) return true;

  const p0 = points[0];
  const edge1 = new THREE.Vector3().subVectors(points[1], p0);
  const edge2 = new THREE.Vector3().subVectors(points[2], p0);
  const normal = new THREE.Vector3().crossVectors(edge1, edge2).normalize();

  return points.every((p, index) => {
    if (index < 3) return true;
    const vectorToPoint = new THREE.Vector3().subVectors(p, p0);
    const distance = Math.abs(vectorToPoint.dot(normal));
    return distance < tolerance;
  });
}

/**
 * Calculate the perimeter of a polygon
 *
 * @param points Array of 3D points defining the polygon
 * @param closed Whether the polygon is closed (default: true)
 * @returns Total length of all edges
 */
export function calculatePolygonPerimeter(points: THREE.Vector3[], closed = true): number {
  if (points.length < 2) return 0;

  let perimeter = 0;
  const numEdges = closed ? points.length : points.length - 1;

  for (let i = 0; i < numEdges; i++) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    perimeter += current.distanceTo(next);
  }

  return perimeter;
}
